import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Button, IconButton } from "@mui/material";
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { postData } from '../../Webservice/Webservice';
import { useNavigate } from "react-router-dom";

const rubikfont = { fontFamily: "Rubik, sans-serif" };

const QUICK_LINKS = [
  { name: "Combo Packs", path: "/combos" },
  { name: "Special Deals", path: "/bogo-deals" },
  { name: "My Orders", path: "/orders" },
  { name: "Wishlist", path: "/wishlist" },
  { name: "FAQs", path: "/faqs" },
  { name: "Support", path: "/support" },
];

const POLICIES = [
  { name: "Terms & Conditions", path: "/terms-and-conditions" },
  { name: "Privacy Policy", path: "/privacy-policy" },
  { name: "Cancellation Policy", path: "/cancellation-policy" },
  { name: "Shipping Policy", path: "/shipping-policy" },
  { name: "Return & Refund Policy", path: "/return-refund-policy" },
  { name: "Payment Policy", path: "/payment-policy" },
  { name: "User Account Policy", path: "/user-account-policy" },
];

const SOCIALS = [
  { Icon: FacebookIcon, label: "Facebook" },
  { Icon: InstagramIcon, label: "Instagram" },
  { Icon: TwitterIcon, label: "Twitter" },
  { Icon: YouTubeIcon, label: "YouTube" },
];

// column heading with a short gold underline
const Heading = ({ children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography sx={{ fontSize: "13px", fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#fff", ...rubikfont }}>
      {children}
    </Typography>
    <Box sx={{ width: 28, height: 2, borderRadius: 2, bgcolor: "#C8A054", mt: 1 }} />
  </Box>
);

// a footer link: the gold line grows in from the left on hover
const FooterLink = ({ children, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "block",
      position: "relative",
      p: 0,
      mb: 1.4,
      border: 0,
      background: "transparent",
      cursor: "pointer",
      textAlign: "left",
      fontSize: "14px",
      color: "rgba(255,255,255,0.68)",
      transition: "color 0.25s ease, transform 0.25s ease",
      ...rubikfont,
      "&::after": { content: '""', position: "absolute", left: 0, bottom: -2, height: "1px", width: 0, bgcolor: "#C8A054", transition: "width 0.3s ease" },
      "&:hover": { color: "#e6c987", transform: "translateX(3px)" },
      "&:hover::after": { width: "100%" },
    }}
  >
    {children}
  </Box>
);

// icon + text row in the contact column
const ContactRow = ({ icon, href, children }) => (
  <Box
    component={href ? "a" : "div"}
    href={href}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      mb: 2,
      textDecoration: "none",
      color: "rgba(255,255,255,0.72)",
      fontSize: "14px",
      lineHeight: 1.7,
      transition: "color 0.25s ease",
      ...rubikfont,
      "&:hover": href ? { color: "#e6c987" } : undefined,
    }}
  >
    <Box sx={{ width: 34, height: 34, borderRadius: "10px", flexShrink: 0, bgcolor: "rgba(200,160,84,0.14)", color: "#C8A054", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 19 } }}>
      {icon}
    </Box>
    <Box sx={{ pt: "5px" }}>{children}</Box>
  </Box>
);

function Footer() {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tokenValue = localStorage.getItem("website_jwt_token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData('category/list', {}, token, false);

        if (response && response.categories && Array.isArray(response.categories)) {
          const activeCategories = response.categories.filter(cat => cat.status === 'active');
          setCategories(activeCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (category) => {
    const categorySlug = category.title?.toLowerCase().replace(/\s+/g, '-') || 'category';
    navigate(`/${categorySlug}`, {
      state: { categoryId: category._id, categoryTitle: category.title }
    });
  };

  const go = (path) => { navigate(path); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        color: "white",
        background: "linear-gradient(160deg, #17110a 0%, #241a0c 55%, #17110a 100%)",
      }}
    >
      {/* soft gold glow in the corner */}
      <Box sx={{ position: "absolute", top: -160, right: -120, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(200,160,84,0.22), transparent 68%)", pointerEvents: "none" }} />

      <Container sx={{ position: "relative", pt: { xs: 5, md: 7 }, pb: 3 }}>
        {/* Help strip */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            p: { xs: 2.5, md: 3 },
            mb: { xs: 5, md: 6 },
            borderRadius: "20px",
            border: "1px solid rgba(200,160,84,0.3)",
            background: "linear-gradient(135deg, rgba(200,160,84,0.16), rgba(200,160,84,0.04))",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: "#C8A054", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SupportAgentIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "17px", fontWeight: 500, ...rubikfont }}>Need help with an order or a product?</Typography>
              <Typography sx={{ fontSize: "13.5px", color: "rgba(255,255,255,0.65)", ...rubikfont }}>Our team is happy to help you.</Typography>
            </Box>
          </Box>
          <Button
            onClick={() => go("/support")}
            sx={{ px: 3, py: 1, borderRadius: "12px", textTransform: "none", fontWeight: 600, color: "#fff", bgcolor: "#C8A054", flexShrink: 0, "&:hover": { bgcolor: "#b38841" }, ...rubikfont }}
          >
            Contact support
          </Button>
        </Box>

        {/* Main Footer Content */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "1.4fr 1fr 1fr 1.2fr" },
            gap: { xs: 4, md: 5 },
            mb: 5,
          }}
        >
          {/* Brand */}
          <Box>
            <Box
              component="img"
              src="/img/tvfmlogo1.png"
              alt="TVFM Logo"
              sx={{ height: 52, width: "auto", mb: 2.5, cursor: "pointer", filter: "brightness(0) invert(1)" }}
              onClick={() => go("/")}
            />
            <Typography sx={{ fontSize: "14px", lineHeight: 1.8, color: "rgba(255,255,255,0.68)", mb: 3, maxWidth: 320, ...rubikfont }}>
              Premium grooming products and professional barber services for the modern gentleman.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              {SOCIALS.map(({ Icon, label }) => (
                <IconButton
                  key={label}
                  component="a"
                  aria-label={label}
                  href={`https://${label.toLowerCase()}.com/yourprofile`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 40,
                    height: 40,
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": { bgcolor: "#C8A054", borderColor: "#C8A054", transform: "translateY(-3px)" },
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* Shop + quick links */}
          <Box>
            <Heading>Shop</Heading>
            {categories.slice(0, 6).map((category) => (
              <FooterLink key={category._id} onClick={() => handleCategoryClick(category)}>{category.title}</FooterLink>
            ))}
            {QUICK_LINKS.slice(0, 2).map((l) => (
              <FooterLink key={l.path} onClick={() => go(l.path)}>{l.name}</FooterLink>
            ))}
          </Box>

          {/* Customer policies */}
          <Box>
            <Heading>Customer Policies</Heading>
            {POLICIES.map((p) => (
              <FooterLink key={p.path} onClick={() => go(p.path)}>{p.name}</FooterLink>
            ))}
          </Box>

          {/* Contact */}
          <Box>
            <Heading>Contact Us</Heading>
            <ContactRow icon={<LocationOnOutlinedIcon />}>
              No-91, 4th Ave, VGN Oval Garden,<br />Vanagaram Main Road,<br />Ambattur, Chennai – 600058, Tamil Nadu
            </ContactRow>
            <ContactRow icon={<PhoneInTalkOutlinedIcon />} href="tel:+919600602093">+91 9600602093</ContactRow>
            <ContactRow icon={<MailOutlineIcon />} href="mailto:glamcare@glamcompany.in">glamcare@glamcompany.in</ContactRow>
          </Box>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            pt: 3,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", textAlign: { xs: "center", md: "left" }, ...rubikfont }}>
            © {new Date().getFullYear()} TVFM Synergy LLP - Designed By Digital Triumph Private Limited💛.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
            {QUICK_LINKS.filter((l) => ["/faqs", "/support"].includes(l.path)).map((l) => (
              <Box
                key={l.path}
                component="button"
                type="button"
                onClick={() => go(l.path)}
                sx={{ p: 0, border: 0, background: "transparent", cursor: "pointer", fontSize: "13px", color: "rgba(255,255,255,0.55)", transition: "color 0.25s ease", ...rubikfont, "&:hover": { color: "#e6c987" } }}
              >
                {l.name === "Support" ? "Contact Us" : l.name}
              </Box>
            ))}
            <IconButton
              aria-label="Back to top"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              sx={{ width: 36, height: 36, color: "#fff", border: "1px solid rgba(255,255,255,0.25)", transition: "all 0.3s ease", "&:hover": { bgcolor: "#C8A054", borderColor: "#C8A054", transform: "translateY(-3px)" } }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
